import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Box,
  Download,
  FileText,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

import { API_URL } from '@/lib/api';
import { downloadOrderReceipt } from '@/lib/receipt';

const statusIcons = {
  pending: Clock,
  confirmed: CheckCircle,
  packed: Box,
  shipped: Truck,
  delivered: CheckCircle,
  cancelled: XCircle
};

const statusColors = {
  pending: 'status-pending',
  confirmed: 'status-confirmed',
  packed: 'bg-blue-100 text-blue-700 border border-blue-200',
  shipped: 'bg-indigo-100 text-indigo-700 border border-indigo-200',
  delivered: 'status-delivered',
  cancelled: 'status-cancelled'
};

const OrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API_URL}/orders/${id}`);
      const d = res.data;
      const itemsTotal = (d.items || []).reduce((s, i) => s + (i.subtotal || 0), 0);
      setOrder({
        id: d.id,
        order_id: d.orderNumber ?? d.order_number,
        invoice_url: d.invoiceUrl ?? d.invoice_url ?? null,
        store_name: d.storeName ?? d.store_name,
        created_at: d.createdAt ?? d.created_at,
        payment_method: String(d.paymentMethod ?? d.payment_method ?? '').toLowerCase(),
        payment_status: String(d.paymentStatus ?? d.payment_status ?? '').toLowerCase(),
        order_status: String(d.orderStatus ?? d.order_status ?? '').toLowerCase(),
        grand_total: d.totalAmount ?? d.total_amount,
        delivery_charge: d.deliveryCharge ?? d.delivery_charge ?? 0,
        items_total: itemsTotal,
        items: (d.items || []).map(i => ({
          product_name: i.product_name,
          variant_name: i.variant_name || i.variant,
          price: i.price,
          quantity: i.quantity,
          subtotal: i.subtotal
        }))
      });
    } catch (error) {
      toast.error('Failed to load order');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!order) return;
    setReceiptLoading(true);
    try {
      const r = await downloadOrderReceipt(order.id, order.order_id, { tryGenerateOnFailure: true });
      toast.success(
        r?.mode === 'hosted' ? 'Invoice PDF opened in a new tab' : 'Receipt downloaded'
      );
      fetchOrder();
    } catch (e) {
      toast.error(e?.message || 'Failed to download receipt');
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!order) return;
    setInvoiceGenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/orders/${id}/generate-invoice`);
      toast.success('Invoice generated and uploaded');
      const invUrl = data?.invoiceUrl ?? data?.invoice_url;
      if (invUrl) {
        window.open(invUrl, '_blank', 'noopener,noreferrer');
      }
      fetchOrder();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setInvoiceGenerating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/orders/${id}`);
      toast.success('Order deleted');
      navigate('/orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete order');
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(`${API_URL}/orders/${id}/status`, { status: newStatus.toUpperCase() });
      toast.success('Order status updated');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const handlePaymentUpdate = async ({ status, method }) => {
    setUpdating(true);
    try {
      await axios.patch(`${API_URL}/orders/${id}/payment-status`, {
        paymentStatus: status?.toUpperCase() || order.payment_status.toUpperCase(),
        paymentMethod: method?.toUpperCase() || order.payment_method.toUpperCase(),
      });
      toast.success('Payment updated');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
      </div>
    );
  }

  if (!order) return null;

  const StatusIcon = statusIcons[order.order_status] || Clock;

  return (
    <div data-testid="order-details-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/orders')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="page-title">Order Details</h1>
          <p className="text-slate-500 mt-1 font-mono">{order.order_id}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            className="bg-purple-950 hover:bg-purple-900 text-white"
            size="sm"
            data-testid="download-receipt-btn"
            disabled={receiptLoading}
            onClick={handleDownloadReceipt}
          >
            {receiptLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download / generate receipt
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="generate-invoice-btn"
            disabled={invoiceGenerating}
            onClick={handleGenerateInvoice}
          >
            {invoiceGenerating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Generate &amp; upload invoice
          </Button>
          {order.invoice_url && (
            <Button variant="secondary" size="sm" asChild>
              <a href={order.invoice_url} target="_blank" rel="noopener noreferrer">
                Open hosted PDF
              </a>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteConfirm(true)}
            className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:border-rose-300"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Order
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Info */}
        <Card>
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${statusColors[order.order_status]}`}>
                <StatusIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <p className="font-medium capitalize">{order.order_status}</p>
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-sm text-slate-500">Order Date</p>
              <p className="font-medium">{new Date(order.created_at).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Store</p>
              <p className="font-medium">{order.store_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Payment Method</p>
              <div className="mt-1">
                <Select
                  value={order.payment_method}
                  onValueChange={(val) => handlePaymentUpdate({ method: val })}
                  disabled={updating}
                >
                  <SelectTrigger className="h-9" data-testid="payment-method-select">
                    <SelectValue className="uppercase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cod">COD</SelectItem>
                    <SelectItem value="online">ONLINE / PREPAID</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-slate-500">Payment Status</p>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={order.payment_status}
                  onValueChange={(val) => handlePaymentUpdate({ status: val })}
                  disabled={updating}
                >
                  <SelectTrigger className="h-9 w-[180px]" data-testid="payment-status-select">
                    <SelectValue className="uppercase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">PENDING</SelectItem>
                    <SelectItem value="success">PAID / SUCCESS</SelectItem>
                    <SelectItem value="cod">COD DUE</SelectItem>
                    <SelectItem value="failed">FAILED</SelectItem>
                  </SelectContent>
                </Select>
                {updating && <Loader2 className="w-4 h-4 animate-spin text-purple-600" />}
              </div>
              <p className="text-[10px] text-slate-400 leading-relaxed uppercase tracking-widest font-bold">
                Balance is settled via Store Wallet payments and chunky deductions.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Products */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Products</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Variant</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.items?.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{item.product_name}</TableCell>
                    <TableCell>{item.variant_name}</TableCell>
                    <TableCell className="text-right">₹{item.price.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right font-medium">₹{item.subtotal.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="mt-6 pt-6 border-t space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Items Total</span>
                <span>₹{order.items_total.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Delivery Charge</span>
                <span>₹{order.delivery_charge.toLocaleString()}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-lg font-bold">
                <span>Grand Total</span>
                <span>₹{order.grand_total.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fulfillment */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Order fulfillment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Order status</p>
              <div className="flex flex-wrap items-center gap-4">
                <Select
                  value={order.order_status}
                  onValueChange={handleStatusUpdate}
                  disabled={updating || order.order_status === 'cancelled' || order.order_status === 'delivered'}
                >
                  <SelectTrigger className="w-48" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="packed">Packed</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                {(order.order_status === 'delivered' || order.order_status === 'cancelled') && (
                  <p className="text-sm text-slate-500">
                    This order is {order.order_status} and cannot be modified.
                  </p>
                )}
                {updating && <Loader2 className="w-4 h-4 animate-spin text-purple-600" />}
              </div>
            </div>
            <p className="text-sm text-slate-500">
              Payment status is set in <span className="font-medium">Order summary</span> above.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Order
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-slate-700">
              Are you sure you want to delete order <strong>{order.order_id}</strong>?
            </p>
            <p className="text-xs text-slate-500 mt-1">
              This will permanently remove the order and all its items. This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {isDeleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDetails;
